<template>
  <div :class="wrapperClasses">
    <RadioButton
      unstyled
      class="radio-button"
      :class="radioButtonClasses"
      v-model="localModelValue"
      :inputId="computedInputId"
      :inputStyle="inputStyle"
      :inputClass="{ 'radio-button-input': true, inputClass }"
      :name="name"
      :value="value"
      :disabled="disabled"
      @change="emitChange"
      @click="emitClick"
      @focus="handleFocus"
      @blur="handleBlur"
      @mousedown="startSelecting"
      @mouseup="stopSelecting"
      @mouseleave="stopSelecting"
      :pt="{
        icon: { class: 'radio-button-icon' },
        box: ({ context }) => ({
          class: ['radio-button-box', context.checked ? 'radio-button-checked' : ''],
        }),
      }"
    />
    <div class="flex flex-col items-start text-button-text-primary-text" :class="textClasses">
      <label :for="computedInputId" class="cursor-pointer">
        <slot />
      </label>
      <span v-if="hint">{{ hint }}</span>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, defineProps, defineEmits, ref } from "vue";
import RadioButton from "primevue/radiobutton";

const props = defineProps({
  id: { type: String, default: null },
  name: { type: String, default: null },
  value: { type: [String, Number, Boolean, Object], default: null },
  modelValue: null,
  inputClass: { type: String, default: null },
  inputStyle: { type: String, default: null },
  disabled: { type: Boolean, default: false },
  hint: { type: String, default: null },
  type: { type: String, default: "primary" },
  size: { type: String, default: "md" },
  iconAlignment: { type: String, default: "left" },
  spacingClass: { type: String, default: "mb-4" },
});

const emit = defineEmits(["click", "update:modelValue", "change", "focus", "blur"]);

const focused = ref(false);
const isSelecting = ref(false);

const localModelValue = computed({
  get: () => props.modelValue,
  set: value => emit("update:modelValue", value),
});

const computedInputId = computed(
  () =>
    props.id ||
    `${props.name}-${typeof props.value === "object" ? props.value?.value : props.value}`,
);

const isSelected = computed(() => {
  if (typeof props.value === "object" && typeof localModelValue.value === "object") {
    return props.value?.value === localModelValue.value?.value;
  }
  return localModelValue.value === props.value;
});

const radioButtonClasses = computed(() => ({
  [`radio-button-${props.type}`]: isSelected.value || isSelecting.value,
  selected: isSelected.value,
  [`radio-button-${props.size}`]: props.size,
  "self-center": ["top", "bottom"].includes(props.iconAlignment),
  "self-start mt-1.5": props.hint,
}));

const textClasses = computed(() => ({
  [`radio-button-${props.type}__text`]: isSelected.value,
}));

const wrapperClasses = computed(() => [
  "flex",
  "gap-2",
  "items-center",
  {
    "flex-row-reverse": props.iconAlignment === "right",
    "flex-col": props.iconAlignment === "top",
    "flex-col-reverse": props.iconAlignment === "bottom",
    disabled: props.disabled,
  },
  props.spacingClass,
]);

const emitChange = (event: Event) => emit("change", event);
const emitClick = (event: MouseEvent) => emit("click", event);

const handleFocus = (event: FocusEvent) => {
  emit("focus", event);
  focused.value = true;
};

const handleBlur = (event: FocusEvent) => {
  emit("blur", event);
  focused.value = false;
};

const startSelecting = () => {
  isSelecting.value = true;
};

const stopSelecting = () => {
  isSelecting.value = false;
};
</script>
